import { v2 } from './v2/index.js';
/**
 * Three pairs proposed to show the system, clearly apart from each other and from the
 * families. Provisional: Eden chooses each course's pair, and nothing stores these.
 */
export const provisionalCoursePalettes = {
    negotiation: { primary: '#c2410c', secondary: '#0ea5e9' },
    pricing: { primary: '#0f766e', secondary: '#fbbf24' },
    leadership: { primary: '#3730a3', secondary: '#fb7185' },
};
const BLACK = '#000000';
const WHITE = '#ffffff';
/** WCAG 2.1's text bar, which a subject hue is held to because it prints words. */
const TEXT_CONTRAST = 4.5;
function channels(hex) {
    return [1, 3, 5].map((at) => Number.parseInt(hex.slice(at, at + 2), 16));
}
function toHex(rgb) {
    return `#${rgb
        .map((value) => Math.round(value).toString(16).padStart(2, '0'))
        .join('')}`;
}
/** `amount` of the way from `from` to `to`, in sRGB. */
function mix(from, to, amount) {
    const a = channels(from);
    const b = channels(to);
    return toHex(a.map((value, i) => value + ((b[i] ?? 0) - value) * amount));
}
/** WCAG 2.1 relative luminance. */
function luminance(hex) {
    const [r, g, b] = channels(hex).map((value) => {
        const unit = value / 255;
        return unit <= 0.039_28 ? unit / 12.92 : ((unit + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
export function contrast(ink, ground) {
    const [bright, dim] = [luminance(ink), luminance(ground)].sort((a, b) => b - a);
    return (bright + 0.05) / (dim + 0.05);
}
/** The course tint behind content: faint enough that text on it reads as on a card. */
function surfaceOf(primary, scheme) {
    return scheme === 'light'
        ? mix(primary, WHITE, 0.88)
        : mix(primary, v2.color.dark.surface.base, 0.84);
}
/**
 * The primary, moved toward black in light and white in dark until text in it reads
 * on every ground it prints on — and white text reads on it in light, since a solid
 * subject fill carries `onSubject` ink. Unchanged when it already reads.
 */
export function readableHue(primary, scheme) {
    const theme = v2.color[scheme];
    const grounds = [
        theme.surface.base,
        theme.surface.raised,
        surfaceOf(primary, scheme),
    ];
    const toward = scheme === 'light' ? BLACK : WHITE;
    for (let step = 0; step <= 50; step += 1) {
        const hue = mix(primary, toward, step / 50);
        if (grounds.every((ground) => contrast(hue, ground) >= TEXT_CONTRAST)) {
            return step === 0 ? primary.toLowerCase() : hue;
        }
    }
    return toward;
}
const ramps = new Map();
/**
 * The six steps a subject family has, derived from a course's pair. `mid` is the
 * secondary untouched in both themes, as a family's `mid` is one value in both: it is
 * the accent nothing is printed on. Cached, so every surface wearing one course shares
 * one derivation and one object.
 */
export function courseRamp(palette, scheme) {
    const primary = palette.primary.toLowerCase();
    const secondary = palette.secondary.toLowerCase();
    const key = `${scheme}|${primary}|${secondary}`;
    const cached = ramps.get(key);
    if (cached)
        return cached;
    const surface = surfaceOf(primary, scheme);
    const hue = readableHue(primary, scheme);
    const shared = { hue, mid: secondary, surface };
    const ground = v2.color.dark.surface.base;
    const ramp = scheme === 'light'
        ? {
            ...shared,
            edge: mix(primary, WHITE, 0.4),
            deep: mix(hue, BLACK, 0.55),
            sink: mix(primary, WHITE, 0.4),
        }
        : {
            ...shared,
            edge: mix(primary, ground, 0.45),
            deep: mix(hue, ground, 0.6),
            sink: mix(primary, ground, 0.88),
        };
    ramps.set(key, ramp);
    return ramp;
}
//# sourceMappingURL=course-palette.js.map